import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  FileSearch,
  Shield,
  Users,
  Globe,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  Map,
  Lock,
  ChevronDown,
  ChevronUp,
  Key,
  ExternalLink,
  Calendar,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "../../button";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// Types
type RuleType = "allow" | "disallow";
type Severity = "critical" | "high" | "medium" | "low" | "info";

interface RobotsRule {
  user_agent: string;
  rule_type: RuleType;
  path: string;
  is_sensitive: boolean;
  sensitivity_reason?: string;
  severity?: Severity;
}

interface SensitivePath {
  path: string;
  category: string;
  severity: Severity;
  reason: string;
}

interface RobotsTxt {
  found: boolean;
  rules: RobotsRule[];
  sitemaps: string[];
  crawl_delay?: number;
  sensitive_paths: SensitivePath[];
  raw_content?: string;
}

interface SitemapEntry {
  url: string;
  lastmod?: string;
  changefreq?: string;
  priority?: number;
}

interface Sitemap {
  found: boolean;
  url: string;
  entries: SitemapEntry[];
  nested_sitemaps: string[];
  total_urls: number;
}

interface SecurityTxt {
  found: boolean;
  contact: string[];
  encryption?: string;
  encryption_key?: string; // Actual PGP key content
  acknowledgments?: string;
  preferred_languages: string[];
  canonical?: string;
  policy?: string;
  hiring?: string;
  expires?: string;
  is_valid: boolean;
  validation_issues: string[];
  raw_content?: string;
}

interface HumansTxt {
  found: boolean;
  raw_content: string;
  team_members: string[];
  site_info: string[];
}

interface WellKnownEntry {
  path: string;
  status_code: number;
  content_type?: string;
  description: string;
  has_content: boolean;
}

interface WellKnownEndpoints {
  discovered: WellKnownEntry[];
}

export interface CrawlHintsModuleData {
  score?: number;
  target?: string;
  base_url?: string;
  robots?: RobotsTxt;
  sitemaps?: Sitemap[];
  security_txt?: SecurityTxt;
  humans_txt?: HumansTxt;
  well_known?: WellKnownEndpoints;
  issues?: Array<string | Issue>;
}

export interface CrawlHintsModuleViewProps {
  data: CrawlHintsModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Severity colors
const SEVERITY_COLORS: Record<Severity, string> = {
  critical: "bg-red-500/15 text-red-400 border-red-500/40",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/40",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  low: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40",
  info: "bg-slate-500/15 text-slate-400 border-slate-500/40",
};

// Raw content toggle component
function RawContentToggle({
  title,
  content,
  icon: Icon = FileText
}: {
  title: string;
  content?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  if (!content) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="absolute top-2 right-2 h-7 w-7 p-0"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
          <pre className="p-4 text-xs font-mono overflow-x-auto bg-muted/20 max-h-[300px] overflow-y-auto">
            {content}
          </pre>
        </div>
      )}
    </div>
  );
}

// PGP Key Display Component
function PgpKeyDisplay({ encryptionUrl, encryptionKey }: { encryptionUrl?: string; encryptionKey?: string }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  if (!encryptionUrl && !encryptionKey) return null;

  const handleCopy = async () => {
    if (encryptionKey) {
      await navigator.clipboard.writeText(encryptionKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-emerald-400" />
          <span className="text-xs text-muted-foreground">Encryption (PGP)</span>
        </div>
        {encryptionUrl && (
          <a
            href={encryptionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
          >
            View Key
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {encryptionKey && (
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between p-2 bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <span className="text-xs text-muted-foreground">Show PGP Key</span>
            {isExpanded ? (
              <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
          {isExpanded && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="absolute top-1 right-1 h-6 w-6 p-0"
              >
                {copied ? (
                  <Check className="h-3 w-3 text-emerald-400" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
              <pre className="p-3 text-[10px] font-mono overflow-x-auto bg-muted/20 max-h-[200px] overflow-y-auto leading-tight">
                {encryptionKey}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Found status indicator
function FoundStatus({ found, label }: { found: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {found ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
      ) : (
        <XCircle className="h-4 w-4 text-slate-400" />
      )}
      <span className={cn("text-sm", found ? "text-foreground" : "text-muted-foreground")}>
        {label}
      </span>
    </div>
  );
}

// Security.txt Card
function SecurityTxtCard({ securityTxt }: { securityTxt?: SecurityTxt }) {
  if (!securityTxt?.found) {
    return (
      <Card className="bg-card/50 border-amber-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-amber-400" />
            <CardTitle className="text-sm">security.txt</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No security.txt found. Consider adding one per RFC 9116.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("bg-card/50", securityTxt.is_valid ? "border-emerald-500/30" : "border-amber-500/30")}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-emerald-400" />
            <CardTitle className="text-sm">security.txt</CardTitle>
          </div>
          <Badge variant="outline" className={cn("text-xs", securityTxt.is_valid ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400")}>
            {securityTxt.is_valid ? "Valid" : "Invalid"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {securityTxt.contact.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Contact</p>
            {securityTxt.contact.map((c, i) => (
              <code key={i} className="block text-xs font-mono bg-muted/50 px-2 py-1 rounded mb-1">
                {c}
              </code>
            ))}
          </div>
        )}
        {securityTxt.policy && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Policy</p>
            <a href={securityTxt.policy} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:underline">
              {securityTxt.policy}
            </a>
          </div>
        )}
        {securityTxt.expires && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Expires</p>
            <span className="text-xs">{securityTxt.expires}</span>
          </div>
        )}
        {/* PGP Key Display */}
        <PgpKeyDisplay
          encryptionUrl={securityTxt.encryption}
          encryptionKey={securityTxt.encryption_key}
        />
        {securityTxt.validation_issues.length > 0 && (
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-amber-400 mb-1">Validation Issues</p>
            {securityTxt.validation_issues.map((issue, i) => (
              <p key={i} className="text-xs text-muted-foreground">• {issue}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Sensitive Paths Table
function SensitivePathsList({ paths }: { paths: SensitivePath[] }) {
  if (paths.length === 0) return null;

  return (
    <div className="space-y-2">
      {paths.map((path, index) => (
        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <div>
              <code className="text-sm font-mono">{path.path}</code>
              <p className="text-xs text-muted-foreground">{path.reason}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{path.category}</Badge>
            <Badge variant="outline" className={cn("text-xs", SEVERITY_COLORS[path.severity])}>
              {path.severity}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "info";
      if (issue.toLowerCase().includes("sensitive") || issue.toLowerCase().includes("admin")) {
        severity = "medium";
      } else if (issue.toLowerCase().includes("missing")) {
        severity = "low";
      }
      return { id: `crawl-issue-${index}`, severity, message: issue, module: "crawl_hints" };
    }
    return issue;
  });
}

export function CrawlHintsModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: CrawlHintsModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const robots = data.robots;
  const securityTxt = data.security_txt;
  const sitemaps = data.sitemaps || [];
  const wellKnown = data.well_known?.discovered || [];

  const totalSitemapUrls = sitemaps.reduce((sum, s) => sum + s.total_urls, 0);
  const sensitivePaths = robots?.sensitive_paths || [];

  return (
    <ModuleContainer
      moduleName="crawl_hints"
      icon={FileSearch}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Discovery Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardContent className="pt-4">
              <FoundStatus found={robots?.found || false} label="robots.txt" />
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4">
              <FoundStatus found={securityTxt?.found || false} label="security.txt" />
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4">
              <FoundStatus found={sitemaps.some(s => s.found)} label="sitemap.xml" />
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-4">
              <FoundStatus found={data.humans_txt?.found || false} label="humans.txt" />
            </CardContent>
          </Card>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Disallow Rules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                {robots?.rules.filter(r => r.rule_type === "disallow").length || 0}
              </p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", sensitivePaths.length > 0 && "border-amber-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Sensitive Paths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", sensitivePaths.length > 0 ? "text-amber-400" : "text-emerald-400")}>
                {sensitivePaths.length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Sitemap URLs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {totalSitemapUrls.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                .well-known
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                {wellKnown.filter(w => w.has_content).length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Security.txt */}
        <SecurityTxtCard securityTxt={securityTxt} />

        {/* Raw File Contents */}
        {(robots?.raw_content || securityTxt?.raw_content) && (
          <ModuleSection
            title="Raw File Contents"
            icon={FileText}
          >
            <div className="space-y-3">
              <RawContentToggle
                title="robots.txt"
                content={robots?.raw_content}
                icon={FileText}
              />
              <RawContentToggle
                title="security.txt"
                content={securityTxt?.raw_content}
                icon={Shield}
              />
            </div>
          </ModuleSection>
        )}

        {/* Sensitive Paths */}
        {sensitivePaths.length > 0 && (
          <ModuleSection
            title="Sensitive Paths Discovered"
            icon={Lock}
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{sensitivePaths.length}</Badge>}
          >
            <SensitivePathsList paths={sensitivePaths} />
          </ModuleSection>
        )}

        {/* Sitemaps */}
        {sitemaps.length > 0 && sitemaps.some(s => s.found) && (
          <ModuleSection
            title="Sitemaps"
            icon={Map}
            badge={<Badge variant="outline" className="text-xs">{totalSitemapUrls} URLs</Badge>}
          >
            <div className="space-y-3">
              {sitemaps.filter(s => s.found).map((sitemap, index) => {
                // Find the most recent lastmod from entries
                const latestEntry = sitemap.entries
                  .filter(e => e.lastmod)
                  .sort((a, b) => new Date(b.lastmod!).getTime() - new Date(a.lastmod!).getTime())[0];

                return (
                  <div key={index} className="p-3 rounded-lg bg-muted/30 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <code className="text-sm font-mono">{sitemap.url}</code>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {sitemap.total_urls} URLs
                      </Badge>
                    </div>
                    {latestEntry?.lastmod && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>Last modified: {new Date(latestEntry.lastmod).toLocaleDateString()}</span>
                      </div>
                    )}
                    {sitemap.nested_sitemaps.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/50">
                        <span className="text-xs text-muted-foreground">
                          {sitemap.nested_sitemaps.length} nested sitemap(s)
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ModuleSection>
        )}

        {/* .well-known Endpoints */}
        {wellKnown.length > 0 && (
          <ModuleSection
            title=".well-known Endpoints"
            icon={Globe}
            badge={<Badge variant="outline" className="text-xs">{wellKnown.length}</Badge>}
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {wellKnown.map((endpoint, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div>
                    <code className="text-sm font-mono">{endpoint.path}</code>
                    <p className="text-xs text-muted-foreground">{endpoint.description}</p>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-xs",
                      endpoint.status_code === 200 ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-500/10 text-slate-400"
                    )}
                  >
                    {endpoint.status_code}
                  </Badge>
                </div>
              ))}
            </div>
          </ModuleSection>
        )}

        {/* humans.txt */}
        {data.humans_txt?.found && (
          <ModuleSection
            title="humans.txt"
            icon={Users}
          >
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              {data.humans_txt.team_members.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-2">Team Members</p>
                  <div className="flex flex-wrap gap-2">
                    {data.humans_txt.team_members.map((member, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{member}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {data.humans_txt.site_info.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Site Info</p>
                  {data.humans_txt.site_info.map((info, i) => (
                    <p key={i} className="text-sm text-muted-foreground">{info}</p>
                  ))}
                </div>
              )}
            </div>
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

CrawlHintsModuleView.displayName = "CrawlHintsModuleView";
